import { Formatter } from '@stamhoofd/utility';
import { AccessRightHelper } from './AccessRight.js';
import { PermissionLevel } from '@stamhoofd/types/PermissionLevel';
import { getPermissionLevelNumber } from './PermissionLevel.js';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
import { isPeriodScopedResourceType, PermissionsResourceKey } from './PermissionsResourceType.js';
import { ResourcePermissions } from './ResourcePermissions.js';
/**
 * Identical to Permissions but with detailed roles, loaded from the organization or platform
 */
export class LoadedPermissions {
    level = PermissionLevel.None;
    resources = new Map();
    accessRights = [];
    constructor(data) {
        Object.assign(this, data);
    }
    static create(data) {
        return new LoadedPermissions(data);
    }
    static fromResource(permissions) {
        return LoadedPermissions.create({
            level: permissions.level,
            resources: new Map(),
            accessRights: [...permissions.accessRights],
        });
    }
    clone() {
        const permissions = LoadedPermissions.create({
            level: this.level,
            resources: new Map(),
            accessRights: [...this.accessRights],
        });
        for (const [type, r] of this.resources) {
            if (!permissions.resources.has(type)) {
                permissions.resources.set(type, new Map());
            }
            for (const [id, resource] of r) {
                permissions.resources.get(type).set(id, resource.clone());
            }
        }
        return permissions;
    }
    /**
     * Returns permissions scoped to the given period. When the period is not the
     * current one (or a period treated as current), $currentPeriod grants are stripped
     * so only $all and explicit resource grants apply.
     */
    forPeriod(isCurrentPeriod) {
        if (isCurrentPeriod) {
            return this;
        }
        const scoped = this.clone();
        for (const resources of scoped.resources.values()) {
            resources.delete(PermissionsResourceKey.CurrentPeriod);
        }
        return scoped;
    }
    static fromRole(role) {
        const permissions = LoadedPermissions.create({
            level: role.level,
            resources: new Map(),
            accessRights: [...role.accessRights],
        });
        for (const [type, r] of role.resources) {
            if (!permissions.resources.has(type)) {
                permissions.resources.set(type, new Map());
            }
            for (const [id, resource] of r) {
                permissions.resources.get(type).set(id, LoadedPermissions.fromResource(resource));
            }
        }
        return permissions;
    }
    static fromResponsibilityRecord(responsibilityRecord, inheritedResponsibilityRoles, allResponsibilites) {
        const responsibility = allResponsibilites.find(r => r.id === responsibilityRecord.responsibilityId);
        if (!responsibility) {
            return LoadedPermissions.create({});
        }
        return this.fromResponsibility(responsibility, responsibilityRecord.groupId, inheritedResponsibilityRoles);
    }
    /**
     * old name: buildRoleForResponsibility
     */
    static fromResponsibility(responsibility, groupId, inheritedResponsibilityRoles) {
        const permissions = responsibility.permissions
            ? this.fromRole(responsibility.permissions)
            : LoadedPermissions.create({});
        if (groupId && responsibility.groupPermissionLevel !== PermissionLevel.None) {
            const map = new Map();
            map.set(groupId, LoadedPermissions.create({ level: responsibility.groupPermissionLevel }));
            permissions.resources.set(PermissionsResourceType.Groups, map);
        }
        const role = inheritedResponsibilityRoles.find(r => r.responsibilityId === responsibility.id && r.responsibilityGroupId === groupId);
        if (role) {
            permissions.add(LoadedPermissions.fromRole(role));
        }
        return permissions;
    }
    static from(permissions, allRoles, inheritedResponsibilityRoles, allResponsibilites) {
        const loaded = LoadedPermissions.create({
            level: permissions.level,
            accessRights: [],
            resources: new Map(),
        });
        for (const [type, r] of permissions.resources) {
            if (!loaded.resources.has(type)) {
                loaded.resources.set(type, new Map());
            }
            for (const [id, resource] of r) {
                loaded.resources.get(type).set(id, LoadedPermissions.fromResource(resource));
            }
        }
        for (const roleRecord of permissions.roles) {
            const role = allRoles.find(a => a.id === roleRecord.id);
            if (role) {
                loaded.add(LoadedPermissions.fromRole(role));
            }
        }
        for (const responsibilityRecord of permissions.responsibilities) {
            if (responsibilityRecord.endDate !== null && responsibilityRecord.endDate < new Date()) {
                continue;
            }
            if (responsibilityRecord.startDate > new Date()) {
                continue;
            }
            const responsibility = allResponsibilites.find(r => r.id === responsibilityRecord.responsibilityId);
            if (!responsibility) {
                continue;
            }
            const r = this.fromResponsibility(responsibility, responsibilityRecord.groupId, inheritedResponsibilityRoles);
            loaded.add(r);
        }
        return loaded;
    }
    getResourcePermissions(type, id) {
        const resource = this.resources.get(type);
        if (!resource) {
            return null;
        }
        const keys = [id];
        if (id !== PermissionsResourceKey.All) {
            keys.push(PermissionsResourceKey.All);
            if (isPeriodScopedResourceType(type) && id !== PermissionsResourceKey.CurrentPeriod) {
                keys.push(PermissionsResourceKey.CurrentPeriod);
            }
        }
        const instances = keys.map(key => resource.get(key)).filter(instance => instance !== undefined);
        if (instances.length === 0) {
            return null;
        }
        return instances.reduce((merged, instance) => merged.merge(instance));
    }
    getMergedResourcePermissions(type, id) {
        const clone = this.clone();
        // Remove this type from resources
        clone.resources.delete(type);
        const r = this.getResourcePermissions(type, id);
        if (r) {
            clone.add(r);
        }
        return clone;
    }
    hasAccess(level) {
        if (getPermissionLevelNumber(this.level) >= getPermissionLevelNumber(level)) {
            // Someone with read / write access for the whole organization, also the same access for each group
            return true;
        }
        return false;
    }
    hasResourceAccess(type, id, level) {
        if (this.hasAccess(level)) {
            return true;
        }
        if (this.getResourcePermissions(type, id)?.hasAccess(level) ?? false) {
            return true;
        }
        return false;
    }
    hasResourceAccessRight(type, id, right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        if (this.getResourcePermissions(type, id)?.hasAccessRight(right) ?? false) {
            return true;
        }
        const autoInherit = AccessRightHelper.autoInheritFrom(right);
        for (const r of autoInherit) {
            if (this.hasResourceAccessRight(type, id, r)) {
                return true;
            }
        }
        return false;
    }
    hasAccessRightForAllResourcesOfType(type, right) {
        return this.hasResourceAccessRight(type, PermissionsResourceKey.All, right);
    }
    hasAccessRightForSomeResource(right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        for (const resource of this.resources.values()) {
            if (resource) {
                for (const r of resource.values()) {
                    if (r.hasAccessRight(right)) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    hasAccessForSomeResourceOfType(type, level) {
        if (this.hasAccess(level)) {
            return true;
        }
        const resource = this.resources.get(type);
        if (resource) {
            for (const r of resource.values()) {
                if (r.hasAccess(level)) {
                    return true;
                }
            }
        }
        return false;
    }
    hasAccessRightForSomeResourceOfType(type, right) {
        if (this.hasAccessRight(right)) {
            return true;
        }
        const resource = this.resources.get(type);
        if (resource) {
            for (const r of resource.values()) {
                if (r.hasAccessRight(right)) {
                    return true;
                }
            }
        }
        return false;
    }
    hasReadAccess() {
        return this.hasAccess(PermissionLevel.Read);
    }
    hasWriteAccess() {
        return this.hasAccess(PermissionLevel.Write);
    }
    hasFullAccess() {
        return this.hasAccess(PermissionLevel.Full);
    }
    hasAccessRight(right) {
        const gl = AccessRightHelper.autoGrantRightForLevel(right);
        if ((gl && this.hasAccess(gl)) || this.accessRights.includes(right)) {
            return true;
        }
        const autoInherit = AccessRightHelper.autoInheritFrom(right);
        for (const r of autoInherit) {
            if (this.hasAccessRight(r)) {
                return true;
            }
        }
        return false;
    }
    add(other) {
        if (getPermissionLevelNumber(other.level) > getPermissionLevelNumber(this.level)) {
            this.level = other.level;
        }
        for (const right of other.accessRights) {
            if (!this.accessRights.includes(right)) {
                this.accessRights.push(right);
            }
        }
        for (const [type, r] of other.resources) {
            for (const [id, resource] of r) {
                if (!this.resources.has(type)) {
                    this.resources.set(type, new Map());
                }
                const current = this.resources.get(type).get(id);
                if (!current) {
                    this.resources.get(type).set(id, resource);
                }
                else {
                    current.add(resource);
                }
            }
        }
    }
    merge(other) {
        const cloned = this.clone();
        cloned.add(other);
        return cloned;
    }
    removeAccessRights(rights) {
        this.accessRights = this.accessRights.filter(r => !rights.includes(r));
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                r.removeAccessRights(rights);
            }
        }
    }
    get isEmpty() {
        if (this.accessRights.length || this.level !== PermissionLevel.None) {
            return false;
        }
        for (const resource of this.resources.values()) {
            for (const r of resource.values()) {
                if (!r.isEmpty) {
                    return false;
                }
            }
        }
        return true;
    }
    getDescription() {
        const stack = [];
        if (this.level === PermissionLevel.Read) {
            stack.push($t(`%m7`));
        }
        if (this.level === PermissionLevel.Write) {
            stack.push($t(`%m8`));
        }
        if (this.level === PermissionLevel.Full) {
            stack.push($t(`%m9`));
            return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
        }
        for (const right of this.accessRights) {
            stack.push(AccessRightHelper.getDescription(right));
        }
        stack.push(...ResourcePermissions.getMapDescription(this.resources));
        if (stack.length === 0) {
            return $t(`%8v`);
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(stack, ', ', ' ' + $t(`%M1`) + ' '));
    }
}
//# sourceMappingURL=LoadedPermissions.js.map